import { FC, PropsWithChildren, useEffect, useMemo, useState } from 'react';

import { Loader } from '@gasbi/shared';

import { useLazyGetCurrentUserQuery } from '@/features/auth/authApi';
import NotAuthorized from '@/pages/NotAuthorized';
import { AppAuthProvider } from '@/providers/AppAuthProvider';

// A brand-new self-registered "potential client" only gets a CounterpartyUserEntity row once
// the cabinet's backend consumes Identity Server's registration integration event. That can
// lag a moment behind the login redirect, so a 403 right here can be a transient race rather
// than proof that this identity has no place in this cabinet. Retry a few times before giving up.
const FORBIDDEN_RETRY_LIMIT = 3;
const FORBIDDEN_RETRY_DELAY_MS = 800;

/**
 * Cookie (BFF) session bootstrap (GSB-7560): one GET /api/user behind the HttpOnly cookie.
 * A 401 is handled by the http interceptor (navigation to /api/login); a 403 means the
 * identity signed in fine but is not a user of this cabinet.
 */
export const AuthInitProvider: FC<PropsWithChildren> = ({ children }) => {
  const [fetchCurrentUser, { data: user, isSuccess, error }] = useLazyGetCurrentUserQuery();
  const [retryCount, setRetryCount] = useState(0);
  const [isForbidden, setIsForbidden] = useState(false);

  const is403 = (error as { status?: number } | undefined)?.status === 403;

  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  useEffect(() => {
    if (!is403) return;

    if (retryCount >= FORBIDDEN_RETRY_LIMIT) {
      setIsForbidden(true);
      return;
    }

    const timeoutId = setTimeout(() => {
      setRetryCount(count => count + 1);
      fetchCurrentUser();
    }, FORBIDDEN_RETRY_DELAY_MS);

    return () => clearTimeout(timeoutId);
  }, [is403, retryCount, fetchCurrentUser]);

  const value = useMemo(
    () =>
      user && {
        user,
        logout: () => {
          window.location.assign('/api/logout?returnUrl=/');
        },
      },
    [user],
  );

  if (isForbidden) {
    return <NotAuthorized />;
  }

  if (!isSuccess || !value) {
    return <Loader />;
  }

  return <AppAuthProvider value={value}>{children}</AppAuthProvider>;
};
